<template>
  <div class="page-layout" :class="{ 'is-mobile': isMobile }">
    <app-header :title="roomTitle" back="" :class="{ 'host-padding': isHost }">
      <toggle-music-volume-button
        v-if="showToggleMusicVolumeButton"
        @sendAction="sendActionToAllGuests"
      />

      <v-btn
        v-if="showSelectAllBtn"
        class="appMain--text mr-2"
        color="primary"
        @click="selectAllParticipants"
      >
        Select All
      </v-btn>

      <actions-nav
        v-if="showActionsNav"
        class="animated fadeIn"
        :actionsMenuItems="actionsMenuItems"
        :selectedParticipantsCount="selectedParticipantsCount"
        :group="group"
      />

      <show-guest-answers-button />

      <v-tooltip left v-if="roomInfo.identity">
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <room-avatar class="ml-2 mr-2" />
          </div>
        </template>
        <span>Settings</span>
      </v-tooltip>
    </app-header>

    <div class="page-layout__content">
      <v-layout class="room-home">
        <room-side-bar
          :room="room"
          :joining-room="joiningRoom"
          :participants="visibleParticipants"
          :mobile-section="mobileSection"
          :disabled="interviewFinished"
          @join="joinRoom"
          @disconnect="disconnectRoom"
        />

        <div
          class="guest-container pb-12"
          v-show="!isMobile || mobileSection === 'participants'"
        >
          <actions-section
            :actions="roomActions"
            :participants="participants"
            :selectedParticipants="selectedParticipants"
            :group="group"
          />

          <div ref="participants">
            <template v-if="chunkedVisibleParticipants.length">
              <div
                v-for="(participants, index) in chunkedVisibleParticipants"
                :key="index"
                class="pt-4 pb-2 pa-4 subscribe-group"
                :class="{
                  selected: subscribeGroup === index,
                }"
              >
                <div
                  class="d-flex"
                  v-if="subscribeGroup !== index"
                  @click="selectSubscribeGroup(index)"
                >
                  <div class="show-group-button mb-3">
                    <icon-base icon="custom/watch" class="mr-4" alt="" />
                    Watch group
                  </div>
                </div>

                <v-row>
                  <participant-card
                    v-for="participant in participants"
                    :key="participant.sid"
                    :participant="participant"
                    :cols="participantColSize"
                    :audioOutputDeviceId="audioOutputDeviceId"
                    @sendCustomAction="sendAction"
                  />
                </v-row>
              </div>
            </template>

            <waiting-connection
              v-else
              :room="room"
              :disabled="interviewFinished"
              @join="joinRoom"
            />
          </div>

          <portal-target name="receivedAction" ref="receivedAction" />

          <room-self-feed
            v-if="isSelfFeedVisible"
            @openSettings="setMobileSection('controls')"
          />
        </div>
      </v-layout>
    </div>

    <audio-notification ref="audio" />
    <end-interview-warning />
    <room-profile-settings ref="profileSettings" />
  </div>
</template>

<script>
import { connect, LocalDataTrack } from 'twilio-video';
import Vue from 'vue';

import AppHeader from '@/components/layouts/AppHeader';
import ParticipantCard from '@/components/rooms/components/roomParticipant/ParticipantCard.vue';
import RoomSideBar from './components/RoomSideBar';
import RoomAvatar from './components/RoomAvatar';
import AudioNotification from './helpers/AudioNotification';
import WaitingConnection from '@/components/rooms/components/WaitingConnection';
import EndInterviewWarning from '@/components/rooms/components/EndInterviewWarning';
import RoomProfileSettings from '@/components/rooms/components/RoomProfileSettings';
import ShowGuestAnswersButton from '@/components/rooms/components/ShowGuestAnswersButton';
import ToggleMusicVolumeButton from '@/components/rooms/components/ToggleMusicVolumeButton';
import RoomSelfFeed from '@/components/rooms/components/RoomSelfFeed.vue';

//actions
import ActionsNav from '@/components/rooms/actions/ActionsNav';
import ActionsSection from '@/components/rooms/actions/ActionsSection';

import { eventBus, trigger, on } from '@/lib/events';
import { differenceInSeconds } from 'date-fns';
import { configureTrackJS } from '@/lib/trackjs';

import analyticSender from './helpers/analyticSender';

import omit from 'lodash/omit';
import debounce from 'lodash/debounce';
import chunk from 'lodash/chunk';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';

import mockParticipants from './data/mockParticipants';
import actionsConfig from './actions/actionsConfig';
import DEFAULT_PARTICIPANT_INFO from './data/participant';
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex';

const isHostIdentity = (identity = '') => {
  return identity.indexOf('host_') === 0;
};

export default {
  name: 'RoomHome',
  components: {
    AppHeader,
    RoomAvatar,
    AudioNotification,
    RoomSideBar,
    RoomProfileSettings,
    WaitingConnection,
    EndInterviewWarning,
    ShowGuestAnswersButton,
    ToggleMusicVolumeButton,
    ActionsNav,
    ActionsSection,
    ParticipantCard,
    RoomSelfFeed,
  },
  data() {
    return {
      participants: [],
      droppedParticipants: [],
      room: null,
      joiningRoom: false,
      audioOutputDeviceId: '',
      dataTrack: null,
      dataTrackDfd: {},
      chunkSize: 12,
      roomActions: [],
      group: {
        id: 'all',
      },
      groupsActionCache: {},
      contextsCache: null,
      subscribeGroup: 0,
      currentCheckpoint: 0,
      mobileSection: 'controls',
      participantsWidth: null,
    };
  },

  computed: {
    ...mapGetters('rooms', ['isHost', 'interviewFinished']),
    ...mapState('rooms', ['roomInfo', 'roomConfig', 'questionnaireAnswers']),

    isSelfFeedVisible() {
      return !this.isHost && this.room && this.isMobile;
    },

    roomTitle() {
      const roomName = this.getInfoParam('room_name', 'the room');
      let title = `Welcome to ${roomName}`;
      if (this.userName) {
        title += `, ${this.userName}`;
      }
      return title;
    },

    userName() {
      return this.getInfoParam('name', '');
    },

    chunkedVisibleParticipants() {
      return chunk(this.visibleParticipants, this.chunkSize);
    },

    showActionsNav() {
      return (
        this.actionsMenuItems.length &&
        this.room &&
        (!this.isMobile || this.mobileSection === 'participants')
      );
    },

    showToggleMusicVolumeButton() {
      if (!this.isHost || !this.room) return false;

      return Object.values(this.groupsActionCache).some((action) => {
        const { type, showMedia, show } = action || {};
        const isQuestionnaireAction = [
          'ActionPreScreen',
          'ActionPostScreen',
        ].includes(type);

        return isQuestionnaireAction && showMedia && show;
      });
    },

    showSelectAllBtn() {
      return this.isHost && this.isEveryoneGroup && this.participants.length;
    },

    /**
     * shows different participants depending on if local
     * user is host or guest
     * @return {[type]} [description]
     */
    visibleParticipants() {
      let participants = this.isHost
        ? this.sortedParticipants
        : this.hostParticipants;

      //filter out participants that aren't part of the current group..
      participants = participants.filter((part) => {
        const { identity = '', groups = [] } = part;
        return isHostIdentity(identity) || groups.indexOf(this.group.id) !== -1;
      });

      return participants.sort((a, b) => b.screenShare - a.screenShare);
    },

    /**
     * get host participants only
     * @return {[type]} [description]
     */
    hostParticipants() {
      return this.participants.filter((part) => {
        return isHostIdentity(part.identity);
      });
    },

    guestParticipants() {
      return this.participants.filter((part) => {
        return !isHostIdentity(part.identity);
      });
    },

    subscribeGroupParticipants() {
      let parts = this.chunkedVisibleParticipants;
      return parts[this.subscribeGroup] || [];
    },

    subscribeGroupParticipantSids() {
      return this.subscribeGroupParticipants.map((part) => part.sid);
    },

    sortedParticipants() {
      return this.participants.sort((a, b) => {
        return (
          isHostIdentity(b.identity) - isHostIdentity(a.identity) ||
          b.order - a.order
        );
      });
    },

    selectedParticipants() {
      return this.filterParticipantsBy('selected', true);
    },

    selectedParticipantsCount() {
      return this.selectedParticipants.length;
    },

    participantColSize() {
      return this.calculateParticipantColSize();
    },

    actionsMenuItems() {
      return this.getActionsMenuItems();
    },

    isEveryoneGroup() {
      return this.group?.id === 'all';
    },
  },

  watch: {
    subscribeGroupParticipantSids: {
      deep: true,
      handler(newList, oldList) {
        if (!isEqual(newList, oldList)) {
          this.updateSubscriptions();
        }
      },
    },
  },

  methods: {
    ...mapActions('rooms', [
      'loadRoomInfo',
      'banRoomGuest',
      'updateRoomSubscriptions',
      'createMeetingToken',
      'addSharedQuestionnaireAnswers',
      'fetchRepInfo',
    ]),

    ...mapMutations('rooms', [
      'setRoomInfo',
      'updateRoomInfo',
      'setQuestionnaireAnswers',
      'setFinishedInterviewInfo',
      'extractStoredQuestionnaireAnswers',
    ]),

    getApiForAction(action) {
      return {
        disconnect: this.disconnectRoom.bind(this),
        send: (data, context = {}) => {
          this.sendAction(data, action.type, context);
        },
        updateParticipantInfo: this.updateParticipantInfo.bind(this),
        setCurrentGroup: this.setCurrentGroup.bind(this),
        removeGroup: this.removeGroup.bind(this),
        select: () => {
          eventBus.trigger('selectAction', action);
        },
        on,
        trigger,
      };
    },

    getFromData() {
      return omit(
        {
          ...this.roomInfo,
          roomName: this.roomInfo.room_name,
        },
        ['token', 'room_token', 'error']
      );
    },

    sendAction(data, actionType, context = {}) {
      data.type = data.type || actionType;
      data.from = this.getFromData();

      const groupId = context?.groupId || this.group.id;

      this.toggleGroupsActionCache(groupId, data);
      if (this.isActionValidForParticipant(data?.to?.identity)) {
        this.sendMessage({ action: data });
      }
    },

    sendActionToAllGuests(data) {
      this.visibleParticipants.forEach((participant) => {
        const { identity, type } = participant;
        if (type === 'guest') {
          this.sendAction({
            ...data,
            to: { identity },
          });
        }
      });
    },

    // don't send action to participant
    // if current group is "everyone" and participant has other groups
    isActionValidForParticipant(identity) {
      if (!this.isEveryoneGroup) return true;

      const participant = this.getParticipantById(identity);
      return !participant?.groups || participant.groups.length === 1;
    },

    removeGroup(group) {
      this.toggleGroupsActionCache(group.id);
      this.roomActions.forEach((action) => {
        action.api.trigger('groupRemoved', group);
      });
    },

    toggleGroupsActionCache(groupId, data) {
      // invites / bans are treated as one-offs and should never be resent over and over.
      const disableCacheFor = [
        'ActionInvites',
        'ActionEndInterview',
        'ActionDropMusicVolume',
      ];

      if (disableCacheFor.includes(data?.type)) return;

      if (!data) {
        delete this.groupsActionCache[groupId];
      } else {
        Vue.set(this.groupsActionCache, groupId, data);
      }
    },

    updateActions(actionsArr) {
      // add or replace actions
      actionsArr.forEach((newAction) => {
        let idx = this.roomActions.findIndex((a) => a.type === newAction.type);
        if (idx < 0) {
          this.roomActions.push(newAction);
        } else {
          this.roomActions.splice(idx, 1, newAction);
        }
      });

      this.roomActions = this.roomActions.map((action) => {
        action.api = this.getApiForAction(action);

        action.api.on('stateChange', (actionStateChange) => {
          actionStateChange.type = action.type;
          this.contextsCache = actionStateChange;
          this.sendMessage({ actionStateChange });
        });

        // update context / action cache
        action.api.on('stateUpdate', ({ contexts }) => {
          this.updateActionsCache(action.type, contexts);
        });

        return action;
      });
    },

    updateActionsCache(actionType, contexts) {
      this.contextsCache = {
        type: actionType,
        contexts,
      };

      (contexts || []).forEach((context) => {
        const { state, groupId } = context || {};
        if (groupId) {
          this.toggleGroupsActionCache(groupId, {
            ...state,
            to: {},
            from: this.getFromData(),
            type: actionType,
          });
        }
      });
    },

    getActionsMenuItems() {
      const type = this.roomInfo.type || 'guest';

      return this.roomActions.filter((action) => {
        const isActionType = (type) => action.type === type;

        if (
          (isActionType('ActionGroup') || isActionType('ActionPrioritize')) &&
          !this.isEveryoneGroup &&
          !this.selectedParticipantsCount
        ) {
          return false;
        }

        // actions for empty room
        const initialActions = [
          'ActionOpenAssignmentBoard',
          'ActionEntertain',
          'ActionVideo',
          'ActionPresentation',
          'ActionPreScreen',
          'ActionPostScreen',
          'ActionScreenShare',
        ];

        if (
          !initialActions.includes(action.type) &&
          !this.participants.length
        ) {
          return false;
        }

        if (isActionType('ActionOpenAssignmentBoard')) {
          const { room } = this.roomInfo || '';
          if (room.includes('-breakout')) {
            return false;
          }
        }

        return action.for.indexOf(type) !== -1;
      });
    },

    selectAllParticipants() {
      this.participants.map((p) => {
        p.selected = true;
        return p;
      });
    },

    getInfoParam(key, def = null) {
      let val = this.roomInfo ? this.roomInfo[key] : this.$route.query[key];
      return typeof val !== 'undefined' ? val : def;
    },

    getMockParticipants() {
      return mockParticipants;
    },

    async getRoomInfo(cache = true) {
      if (this.interviewFinished) return;

      if (!isEmpty(this.roomInfo) && cache) {
        return this.roomInfo;
      }

      const config = await this.loadRoomInfo(this.$route.query);

      if (config) {
        this.onRoomInfoLoad();
      }
      return config;
    },

    onRoomInfoLoad() {
      this.updateActions(this.roomConfig?.actions || actionsConfig);
      this.setTitle();

      const userId = (this.roomInfo?.identity || '').replace('host_', '');
      configureTrackJS(userId);

      if (this.roomInfo.ttl) {
        this.checkDuration();
      }

      setTimeout(() => {
        analyticSender('visited_room');
      }, 100);

      this.runCheckpointSender();
    },

    generateBandwidthConfig(config) {
      return {
        ...config,
        //we join first then publish tracks now
        // audio: true,
        // video: { height: 720, frameRate: 24, width: 1280 },
        bandwidthProfile: {
          video: {
            mode: 'collaboration',
            // maxTracks: 5,
            dominantSpeakerPriority: 'standard',
            renderDimensions: {
              high: { height: 1080, width: 1980 },
              standard: { height: 720, width: 1280 },
              low: { height: 176, width: 144 },
            },
          },
        },
        dominantSpeaker: this.isHost,
        maxAudioBitrate: 16000, //For music remove this line
        preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
        networkQuality: { local: 1, remote: 1 },
        automaticSubscription: false,
      };
    },

    setMobileSection(section) {
      if (this.mobileSection === section) {
        return;
      }

      this.mobileSection = section;
      eventBus.trigger('sectionShown', section);
    },

    async connectToRoom(config = {}) {
      config.tracks = config.tracks || [];
      config.name = this.roomInfo.room;
      config.tracks.push(this.createLocalDataTrack());

      const room = await connect(
        this.roomInfo.room_token,
        this.generateBandwidthConfig(config)
      );

      this.setRoom(room);
      this.joiningRoom = false;

      this.setupLocalDataTrackPromise(room);
      this.shareInfo();
      this.setupInfoShare();
      eventBus.trigger('startTracks');

      if (this.isMobile) {
        this.setMobileSection('participants');
      }

      if (this.isHost) {
        this.extractStoredQuestionnaireAnswers();
      }

      analyticSender('joined_room');
    },

    setRoom(room) {
      this.room = room;

      room.on('participantConnected', (participant) => {
        if (this.roomConfig.chime && this.isHost) {
          this.$refs.audio.playAudio();
        }

        this.ensureParticipant(participant);
      });

      room.on('participantDisconnected', (participant) => {
        this.dropParticipant(participant);
      });

      room.on('disconnected', () => {
        this.participants = [];
      });

      room.participants.forEach((participant) => {
        this.ensureParticipant(participant);
      });

      if (this.getInfoParam('mock') === '1') {
        this.getMockParticipants().forEach((part) => {
          this.ensureParticipant(part);
        });
      }
    },

    ensureParticipant(part) {
      let existing = this.participants.find((p) => p.sid === part.sid);

      if (existing) return false;

      //make sure these keys are attached
      //to the participant ahead of time for reactivity
      for (let key in DEFAULT_PARTICIPANT_INFO) {
        part[key] = part[key] || DEFAULT_PARTICIPANT_INFO[key];
      }

      let cachedParticipantInfo = this.droppedParticipants.find((dp) => {
        return dp.identity === part.identity;
      });

      if (cachedParticipantInfo) {
        part.groups = cachedParticipantInfo.groups;
        if (!part.name) {
          part.name = cachedParticipantInfo.name;
        }

        if (!part.info) {
          part.info = cachedParticipantInfo.info;
        }
      }

      let inCurrentGroup = part.groups.indexOf(this.group.id) !== -1;

      part.hidden = !inCurrentGroup;
      part.muted = !isHostIdentity(part.identity);
      part.type = isHostIdentity(part.identity) ? 'host' : 'guest';

      this.manageParticipantTracks(part);

      this.participants.push(part);

      if (!part?.info) {
        this.addRepInfo(part.identity);
      }

      this.setTitle();
    },

    async addRepInfo(repId) {
      const isVector = window.location.hostname.includes('vmcteam.com');
      if (!isVector || !repId) return;

      try {
        const repInfo = await this.fetchRepInfo(repId);
        const rep = this.participants.find(({ identity }) => {
          return identity === repId;
        });

        if (rep && repInfo?.info) {
          rep.info = repInfo.info;
        }
      } catch (error) {
        console.log(`Failed to add rep info for ID ${repId}:`, error);
      }
    },

    async checkCachedActionsForParticipant(part) {
      if (isHostIdentity(part.identity)) {
        // send actions contexts
        if (this.contextsCache) {
          this.sendMessage({ actionStateChange: this.contextsCache });
        }

        // send saved questionnaire answers
        if (this.questionnaireAnswers.length) {
          this.sendQuestionnaireAnswers(part, this.questionnaireAnswers);
        }
      }

      const partGroupId =
        part.groups.find((groupId) => groupId !== 'all') || 'all';

      let action = cloneDeep(this.groupsActionCache[partGroupId]);
      if (action) {
        action.to.identity = part.identity;
        this.sendMessage({ action });
      }
    },

    // send questionnaire answers to hosts who joined later
    sendQuestionnaireAnswers(part, answers) {
      if (!answers.length) return;

      const shareAnswers = {
        answers: JSON.stringify(answers),
        to: { identity: part.identity },
      };

      this.sendMessage({ shareAnswers });
    },

    manageParticipantTracks(part) {
      if (!part.tracks) return;

      part.on('trackSubscribed', (track) => {
        this.onTrackSubscribed(part, track);
      });

      part.on('trackUnsubscribed', (track) => {
        let partComponent = part.element;
        if (partComponent) {
          partComponent.removeTrackElementByKind(track);
        }
      });

      setTimeout(() => {
        part.tracks.forEach((track) => {
          this.setParticipantTrackPriority(part, track);

          if (track.isSubscribed && part.element) {
            part.element.attachTrack(track);
          }
        });
      });
    },

    onTrackSubscribed(part, track) {
      this.setParticipantTrackPriority(part, track);

      // screenshare
      if (track.name.endsWith('_sv') || track.name.endsWith('_sa')) {
        eventBus.trigger('shareTracksUpdated', true);
        return;
      }

      if (track.kind === 'data') {
        track.on('message', (e) => this.handleDataTrackEvent(e, part));
        setTimeout(() => {
          this.sendMessageTo(part, 'subscribedToDataTrack');
        });
        return;
      }

      let partComponent = part.element;
      if (partComponent) {
        partComponent.attachTrack(track);
      }
    },

    parseData(data) {
      try {
        return JSON.parse(data);
      } catch (e) {
        return {};
      }
    },

    handleDataTrackEvent(data, part) {
      data = this.parseData(data);
      const checkEventType = (type) => {
        return !!data[type];
      };

      switch (true) {
        // update participant info
        case checkEventType('info'): {
          if (!data.info.identity) {
            data.info.identity = part.identity;
          }

          eventBus.trigger('participantInfoUpdated', {
            info: data.info,
          });
          break;
        }

        // fire received action
        case checkEventType('action'): {
          if (data.action.type === 'ActionEndInterview') {
            this.endInterviewForGuest(data.action);
            return;
          }

          if (data.action.type === 'ActionDropMusicVolume') {
            eventBus.trigger('dropMusicVolume', data.action?.dropVolume);
            return;
          }

          eventBus.trigger('participantAction', data.action);
          break;
        }

        // update action state
        case checkEventType('actionStateChange'): {
          eventBus.trigger('actionStateChange', data.actionStateChange);
          break;
        }

        case checkEventType('shareAnswers'): {
          const { answers, to } = data?.shareAnswers || {};
          if (to?.identity === this.roomInfo.identity && !!answers) {
            this.addSharedQuestionnaireAnswers(JSON.parse(answers));
          }

          break;
        }

        // send cached action from host to fresh guests
        case checkEventType('subscribedToDataTrack'): {
          const subscribedTo = data?.subscribedToDataTrack?.to?.identity;
          if (subscribedTo != this.roomInfo.identity) return;

          if (this.isHost) {
            this.checkCachedActionsForParticipant(part);
          } else {
            this.sendMessageTo(part, 'subscribedToDataTrack');
          }
        }
      }
    },

    // ban / end interview
    endInterviewForGuest(action) {
      const { room, identity } = this.roomInfo;
      const { reason } = action || {};

      if (action?.to?.identity !== identity) return;

      this.banRoomGuest({ room, identity });

      // ban guest in the main room
      if ((room || '').includes('-breakout')) {
        const mainRoom = room.split('-breakout')[0];
        this.banRoomGuest({
          room: mainRoom,
          identity,
        });
      }

      this.setFinishedInterviewInfo({ identity, reason });

      this.disconnectRoom();
    },

    setParticipantTrackPriority(part, track) {
      if (!track.isEnabled) return;

      //if current user is not a host and the participant
      //is not a host, let's set the track priority low

      if (!this.isHost && !isHostIdentity(part.identity)) {
        console.log('setPriority:low', {
          part,
          track,
        });
        track.setPriority('low');
      }
    },

    getParticipantById(identity) {
      return this.participants.find((p) => p.identity === identity);
    },

    updateSubscriptions: debounce(function () {
      if (!this.room) return;

      let sids = this.subscribeGroupParticipantSids;
      if (!sids.length && !this.isHost) {
        return;
      }

      let data = {
        room_id: this.room.sid,
        participant_id: this.room.localParticipant.sid,
      };

      if (this.isHost) {
        data.kinds = ['audio', 'data'];
        data.track_ids = [];
        //get video tracks of subscribeGroup
        this.subscribeGroupParticipants.forEach((part) => {
          data.track_ids.push(part.identity + '_v');
          if (isHostIdentity(part.identity)) {
            data.track_ids.push(part.identity + '_sv');
          }
        });
      } else {
        data.publisher_ids = sids;
      }

      this.updateRoomSubscriptions(data);
    }, 100),

    dropParticipant(part) {
      let dropped = this.droppedParticipants.filter((dp) => {
        return dp.identity !== part.identity;
      });

      dropped.push({
        identity: part.identity,
        groups: part.groups,
        name: part.name,
        info: part.info,
      });

      this.droppedParticipants = dropped;
      this.participants = this.participants.filter((p) => p.sid !== part.sid);
      this.setTitle();
    },

    updateParticipantAssignment(update) {
      const { assignment_status, assignment_order, from } = update;

      if (update.sendOrderChangedEvent) {
        analyticSender('assignment_order_changed', {
          order: assignment_order,
          assignment_status,
        });
        return;
      }

      if (update.sendKeptOrRejectedEvent) {
        const currentStatus = this.roomInfo.assignment_status;
        const isRejected = currentStatus === 'rejected';
        const isKept = !isRejected && currentStatus;
        const eventName = isRejected ? 'rejected' : isKept ? 'kept' : null;

        if (eventName) {
          analyticSender(eventName);
        }

        return;
      }

      if (this.roomInfo.assignment_status !== assignment_status) {
        analyticSender('assignment_changed', {
          assignment_status,
          from_identity: from,
        });
      }

      this.updateRoomInfo({
        assignment_status,
        assignment_order,
      });
    },

    //do not put info.to into info from the origin
    updateParticipantInfo(info, options) {
      let identity = info.identity;

      if (info.identity === this.roomInfo.identity && !this.isHost) {
        this.updateParticipantAssignment(info);
        return;
      }

      let part = this.getParticipantById(identity);

      if (part) {
        // send cached action to participant on group change
        if (info.groups && !isEqual(part.groups, info.groups)) {
          this.checkCachedActionsForParticipant(info);
        }

        //using defaultInfo keys because if you just loop over
        //info you might accidentally try to set a key with no setter
        for (let key in DEFAULT_PARTICIPANT_INFO) {
          if (typeof info[key] !== 'undefined') {
            part[key] = info[key];
          }
        }

        // check office number
        if (info.on) {
          part.officeNumber = info.on;
        }
      }

      if (options && options.to) {
        info._to = options.to;
        //we do this to try to let action state
        //catch up first before participant state is changed
        setTimeout(() => {
          this.sendMessage({ info });
        }, 500);
      }
    },

    setCurrentGroup(group) {
      this.group = group;
      this.subscribeGroup = 0;
    },

    filterParticipantsBy(key, value) {
      return this.participants.filter((p) => {
        return p[key] === value;
      });
    },

    async joinRoom(config) {
      this.joiningRoom = true;
      const info = await this.getRoomInfo(true);
      if (!info) {
        this.joiningRoom = false;
        return;
      }
      this.connectToRoom(config);
    },

    disconnectRoom() {
      if (!this.room) return false;

      if (!this.isHost) {
        eventBus.trigger('clearActionsStates');
      }

      analyticSender('left_room');

      // stop data track & disconnect
      (this.room.localParticipant?.tracks || []).forEach((trackPub) => {
        this.room.localParticipant.unpublishTrack(trackPub.track);
      });
      this.room.disconnect();
      this.room = null;
      this.dataTrack = null;
      this.dataTrackDfd = {};
    },

    createLocalDataTrack() {
      if (this.dataTrack) {
        return this.dataTrack;
      }

      return (this.dataTrack = new LocalDataTrack());
    },

    setupLocalDataTrackPromise(room) {
      const dfd = {};
      room = room || this.room;

      dfd.promise = new Promise((resolve, reject) => {
        dfd.resolve = resolve;
        dfd.reject = reject;
      });

      room.localParticipant.on('trackPublished', (publication) => {
        if (publication.track === this.dataTrack) {
          dfd.resolve();
        }
      });

      room.localParticipant.on('trackPublicationFailed', (error, track) => {
        if (track === this.dataTrack) {
          if (error.code && error.code === 53205) {
            this.forceDisconnect('alreadyJoined');
          }
          dfd.reject(error);
        }
      });

      this.dataTrackDfd = dfd;
    },

    sendMessage(message) {
      if (isEmpty(this.dataTrackDfd) || !this.room) return;

      this.dataTrackDfd.promise.then(() => {
        this.dataTrack.send(JSON.stringify(message));
      });
    },

    sendMessageTo(part, messageKey, payload = {}) {
      payload.to = {
        identity: part.identity,
      };

      payload.from = {
        identity: this.roomInfo.identity,
      };

      this.sendMessage({
        [messageKey]: payload,
      });
    },

    shareInfo() {
      const omitInfoKeys = ['token', 'room_token', 'room', 'id', 'error'];
      const info = omit(this.roomInfo, omitInfoKeys);
      if (info.identity) {
        this.sendMessage({ info });
      }
    },

    eventuallyShareInfo: debounce(function () {
      this.shareInfo();
    }, 1000),

    setupInfoShare() {
      if (this.dataShareInterval) {
        clearTimeout(this.dataShareInterval);
      }
      this.dataShareInterval = setInterval(() => {
        this.shareInfo();
      }, 3000);
    },

    setTitle() {
      let title = this.roomInfo.room_name || 'The Room ';
      if (this.isHost) {
        title = `(${this.participants.length}) ${title}`;
      }
      window.document.title = title;
    },

    checkDuration() {
      if (this.isMeetingEnded()) return false;

      let durationInterval = setInterval(() => {
        if (this.isMeetingEnded()) {
          this.forceDisconnect('ended');
          clearInterval(durationInterval);
        }
      }, 1000);
    },

    isMeetingEnded() {
      const { start_time, ttl } = this.roomInfo || {};
      const endTimestamp = (start_time + ttl) * 1000;
      const currentTime = +new Date();

      return currentTime > endTimestamp;
    },

    forceDisconnect(type) {
      this.disconnectRoom();
      eventBus.trigger('showRoomInfoDialog', { type });
    },

    selectSubscribeGroup(index) {
      this.subscribeGroup = index;
    },

    runCheckpointSender() {
      const MAX_LENGTH = 30;
      const CHECK_INTERVAL = 5;
      const CHECK_INTERVAL_IN_SEC = CHECK_INTERVAL * 60;

      const startTime = this.roomInfo.start_time;
      const savedCheckpoint = this.getSavedCheckpoint();

      if (!startTime || savedCheckpoint === MAX_LENGTH || this.isHost) {
        return;
      }

      //we only want to produce checkpoints that report
      //on those that are actually joined to the room vs just sitting
      //on the page and not connected.
      if (!this.room) {
        return this.rerunCheckpoint(1000);
      }

      const playingTimeInSec = differenceInSeconds(
        new Date(),
        new Date(startTime * 1000)
      );

      // if from start > 30 minutes save and exit
      if (playingTimeInSec > MAX_LENGTH * 60) {
        this.sendCheckpoint(MAX_LENGTH);
        return;
      }

      // wait for the start time
      if (playingTimeInSec < 0) {
        const timeToStartInMs = Math.abs(playingTimeInSec) * 1000;
        return this.rerunCheckpoint(timeToStartInMs);
      }

      if (playingTimeInSec >= 0) {
        const currentCheckpoint =
          Math.floor(playingTimeInSec / CHECK_INTERVAL_IN_SEC) * CHECK_INTERVAL;
        // if saved checkpoint outdated or the meeting just started
        if (
          currentCheckpoint > savedCheckpoint ||
          (currentCheckpoint === 0 && playingTimeInSec < 10)
        ) {
          this.sendCheckpoint(currentCheckpoint || 0);
        }

        const nextCheckpointRest =
          CHECK_INTERVAL_IN_SEC - (playingTimeInSec % CHECK_INTERVAL_IN_SEC);
        this.rerunCheckpoint(nextCheckpointRest * 1000);
      }
    },

    rerunCheckpoint(delay) {
      setTimeout(() => {
        this.runCheckpointSender();
      }, delay);
    },

    getSavedCheckpoint() {
      return +sessionStorage.getItem(this.getCheckpointStoreName()) || 0;
    },

    sendCheckpoint(checkpoint) {
      let props = { minutes: checkpoint };
      analyticSender('checkpoint', props);
      sessionStorage.setItem(this.getCheckpointStoreName(), checkpoint);
    },

    getCheckpointStoreName() {
      return `checkpoint-event-${this.getStoreKey()}.`;
    },

    getStoreKey() {
      const identity = this.roomInfo.identity.replace(/\_/g, '-');
      const roomId = this.roomInfo.room;
      return `${identity}-${roomId}`;
    },

    setParticipantsWidth() {
      let el = this.$refs.participants;
      if (el) {
        this.participantsWidth = el.clientWidth;
      }
    },

    calculateParticipantColSize() {
      const isMediaVisible = !isEmpty(this.$refs?.receivedAction?.passengers);

      let size = this.$vuetify.breakpoint.name;
      let count = this.visibleParticipants.length;

      let xs = size === 'xs';
      let sm = size === 'sm';

      if (this.isHost && sm && this.participantsWidth < 380) {
        return 12;
      }

      if (!this.isHost && isMediaVisible) {
        if (xs) return count === 1 ? 12 : 6;
        return xs ? 6 : 3;
      }

      if (count === 1) return 12;
      if (count < 6 || sm) return 6;
      if (size === 'lg') return 4;
      if (size === 'xl') return this.participantsWidth < 1600 ? 3 : 2;
      return 12;
    },

    // extract answers received in breakout rooms
    // and share them between hosts
    async refreshQuestionnaireAnswers() {
      if (!this.isHost) return;

      this.extractStoredQuestionnaireAnswers();
      await this.$nextTick();
      this.hostParticipants.forEach((host) => {
        this.sendQuestionnaireAnswers(host, this.questionnaireAnswers);
      });
    },

    handleVisibilityChange() {
      const isVisible = document.visibilityState === 'visible';
      if (isVisible) {
        this.refreshQuestionnaireAnswers();
      }
    },
  },

  created() {
    this.getRoomInfo();

    //on/trigger needs to use this context so using old self reference
    eventBus.on('audioOutputChanged', ({ deviceId }) => {
      this.audioOutputDeviceId = deviceId;
    });

    eventBus.on('participantInfoUpdated', ({ info, options }) => {
      this.updateParticipantInfo(info, options);
    });

    eventBus.on('shareScreenVideo', (screenTrack) => {
      this.setRoomInfo({ ...this.roomInfo, screenShare: !!screenTrack });
      if (screenTrack) {
        const screenTrackSettings = screenTrack.getSettings();
        analyticSender('shared_screen', screenTrackSettings);
      }
    });

    eventBus.on('loadInfo', () => {
      this.getRoomInfo(false);
    });

    eventBus.on('joinRoom', this.joinRoom.bind(this));

    eventBus.on('sectionShown', this.setMobileSection);

    window.addEventListener('visibilitychange', this.handleVisibilityChange);

    window.addEventListener('resize', () => {
      this.setParticipantsWidth();
    });

    window.addEventListener('beforeunload', this.disconnectRoom);

    window.eventBus = eventBus;
  },
};
</script>

<style lang="less" src="@/assets/styles/rooms/rooms-home.less"></style>
<style lang="less" src="@/assets/styles/basic/page-layout.less"></style>
